import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

// Helper function to create word dictionary
function createWordDict(words: any[]) {
  const dict: { [key: number]: any } = {};
  words.forEach((word) => {
    dict[word.id] = word;
  });
  return dict;
}

const QuestionVocabIntroPage = ({
  hearts,
  onNext,
  onBack,
  progress,
  isLast,
  onValidate,
  onAnswer,
  darkMode = false,
  questionConfig,
  vocabularyBank,
  lessonData,
}: {
  hearts: any;
  onNext: any;
  onBack: any;
  progress: any;
  isLast: any;
  onValidate: any;
  onAnswer: any;
  darkMode?: boolean;
  questionConfig?: any;
  vocabularyBank?: any[];
  lessonData?: any;
}) => {
  // Use lesson-specific data if available, otherwise fallback to imports
  const wordDict = vocabularyBank ? createWordDict(vocabularyBank) : {};
  const introWordId = questionConfig?.introWordId || 105; // fallback ID
  const introWord = wordDict[introWordId] || {
    id: introWordId,
    batak: "ᯀᯯ",
    latin: ["au"],
    indonesia: "Saya",
    syllable_mapping: [{ batak_char: "ᯀᯯ", latin: "au" }],
  };
  const characterImg = useRandomCharacterImage([introWordId]);

  const handleContinue = () => {
    // For vocab intro, we can give a small XP reward for viewing
    if (onAnswer) onAnswer(0, 1, 1); // (xp, correct, total)
    if (onValidate) onValidate();
    if (onNext) onNext();
  };

  return (
    <QuestionPageLayout
      hearts={hearts}
      onBack={onBack}
      progress={progress}
      darkMode={darkMode}
    >
      <div className="flex flex-col items-center max-w-4xl mx-auto md:space-y-8 space-y-5">
        {/* Header */}
        <div className="text-center">
          <h2
            className={`text-3xl font-bold mb-4 ${
              darkMode ? "text-gray-100" : "text-gray-700"
            }`}
            style={{ fontFamily: "Feather Bold" }}
          >
            Kata Baru
          </h2>
        </div>

        {/* Mascot with Speech Bubble */}
        <div className="flex flex-col items-center md:mb-10 mb-6">
          <div className="flex items-center justify-center md:gap-6 gap-3 md:mb-10 mb-5">
            <img
              src={characterImg}
              alt="Characters"
              className="md:w-32 md:h-32 w-20 h-20 object-contain"
            />
            <div className="relative flex items-end">
              <div
                className={`rounded-xl md:px-6 md:py-4 px-3 py-2 shadow-lg text-center min-w-[120px] md:min-w-[200px] border relative ${
                  darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
                }`}
              >
                <p
                  className={`md:text-lg text-base ${
                    darkMode ? "text-gray-100" : "text-gray-700"
                  }`}
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  Mari pelajari kata{" "}
                  <span className="font-bold text-blue-400">
                    {introWord.indonesia}
                  </span>{" "}
                  dalam bahasa Batak!
                </p>
                {/* Bubble tail */}
                <div
                  className={`absolute -left-2 bottom-5 md:w-4 md:h-4 w-3 h-3 rounded-sm rotate-45 ${darkMode
                      ? "bg-gray-800 border-l border-b border-gray-700"
                      : "bg-white border-l border-b border-gray-200"
                    }`}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          className={`rounded-2xl md:p-8 p-4 border-2 shadow-lg w-full max-w-2xl ${darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-blue-50 border-blue-200"
            }`}
        >
          <div className="text-center md:space-y-6 space-y-3">
            {/* Batak Script (syllable breakdown, styled like GuidePage) */}
            <div className="flex justify-center md:gap-2 gap-1 mb-3">
              {(introWord.syllable_mapping ?? []).map((syll, idx) => (
                <span key={idx} className="flex flex-col items-center">
                  <span
                    className={`md:text-xs text-[10px] font-semibold leading-none md:mb-3 mb-1 ${
                      darkMode ? "text-blue-300" : "text-blue-700"
                    }`}
                    style={{ fontFamily: "DIN Next Rounded" }}
                  >
                    {syll.latin}
                  </span>
                  <span
                    className={`md:text-5xl text-3xl font-bold tracking-wide ${
                      darkMode ? "text-blue-100" : "text-blue-900"
                    }`}
                    style={{ fontFamily: "Noto Sans Batak" }}
                  >
                    {syll.batak_char}
                  </span>
                </span>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center justify-center md:space-x-4 space-x-2">
              <div
                className={`$${
                  darkMode ? "bg-blue-800" : "bg-blue-300"
                } h-0.5 flex-1`}
              ></div>
              <div
                className={`text-sm font-medium ${
                  darkMode ? "text-blue-300" : "text-blue-400"
                }`}
              >
                artinya
              </div>
              <div
                className={`$${
                  darkMode ? "bg-blue-800" : "bg-blue-300"
                } h-0.5 flex-1`}
              ></div>
            </div>

            {/* Indonesian Translation */}
            <div>
              <div
                className={`md:text-3xl text-xl font-bold mb-2 ${
                  darkMode ? "text-gray-100" : "text-gray-800"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                {introWord.indonesia}
              </div>
            </div>
          </div>
        </div>

        {/* Character Breakdown */}
        <div className="w-full max-w-2xl flex flex-col items-center">
          <h3
            className={`md:text-lg text-base font-bold mb-4 text-center ${
              darkMode ? "text-gray-100" : "text-gray-700"
            }`}
            style={{ fontFamily: "Feather Bold" }}
          >
            Pembagian Karakter
          </h3>
          <div className="inline-flex justify-center md:gap-2 gap-1">
            {introWord.syllable_mapping.map((mapping, idx) => (
              <div
                key={idx}
                className={`rounded-lg md:p-4 p-2 shadow-sm text-center border ${darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                  }`}
                style={{ minWidth: 60, maxWidth: 100 }}
              >
                <div
                  className={`md:text-2xl text-lg font-bold ${
                    darkMode ? "text-blue-100" : "text-gray-600"
                  }`}
                  style={{ fontFamily: "Noto Sans Batak" }}
                >
                  {mapping.batak_char}
                </div>
                <div
                  className={`md:text-lg text-sm ${
                    darkMode ? "text-blue-300" : "text-gray-600"
                  }`}
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  {mapping.latin}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className={`md:px-8 px-5 md:py-3 py-2 rounded-lg font-bold md:text-base text-sm transition-colors shadow-lg ${darkMode
                ? "bg-blue-700 hover:bg-blue-800 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {isLast ? "Selesai" : "Lanjutkan"}
          </button>
        </div>
      </div>
    </QuestionPageLayout>
  );
};

export default QuestionVocabIntroPage;
